/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  FocusEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react';
import Swal from 'sweetalert2';

import { ICategoryData } from '..';
import Input from '~/components/Input';
import { KrTrash } from '~/components/KoroIcons';
import Toast from '~/utils/toast';
import { HiCheck } from 'react-icons/hi';
import { FaArrowsAltV } from 'react-icons/fa';

interface ICategory {
  category: ICategoryData;
  value?: string;
  sold?: boolean;
  child?: boolean;
  elderly?: boolean;
  male?: boolean;
  female?: boolean;
  qty_min?: number;
  has_kit?: boolean;
  has_age_group?: boolean;
  min_age?: number;
  max_age?: number;
  onFocus?: FocusEventHandler<any> | undefined;
  onBlur?: FocusEventHandler<any> | undefined;
  onChangeCategory(value: string): void;
  onChangeQtyMin(value: number): void;
  onChangeChild(value: boolean): void;
  onChangeHasAgeGroup(value: boolean): void;
  onChangeElderly(value: boolean): void;
  onChangeFemale(value: boolean): void;
  onChangeMale(value: boolean): void;
  onChangeHasKit(value: boolean): void;
  onRemoveCategory(): void;
  onChangeMinAge(value: number): void;
  onChangeMaxAge(value: number): void;
}

const Category: React.FC<ICategory> = ({
  category,
  value,
  sold,
  child,
  elderly,
  male,
  female,
  qty_min,
  has_kit,
  has_age_group,
  min_age,
  max_age,
  onFocus,
  onBlur,
  onChangeCategory,
  onChangeQtyMin,
  onChangeChild,
  onChangeElderly,
  onChangeHasAgeGroup,
  onChangeMale,
  onChangeFemale,
  onChangeHasKit,
  onRemoveCategory,
  onChangeMinAge,
  onChangeMaxAge,
}) => {
  const [valueSelected, setValueSelected] = useState('');
  const [checkChild, setCheckChild] = useState(!!child);
  const [checkElderly, setCheckElderly] = useState(!!elderly);
  const [checkMale, setCheckMale] = useState(!!male);
  const [checkFemale, setCheckFemale] = useState(!!female);
  const [qtyMin, setQtyMin] = useState(qty_min);
  const [hasKit, setHasKit] = useState(has_kit);
  const [hasAgeGroup, setHasAgeGroup] = useState(has_age_group);
  const [minAge, setMinAge] = useState<number | undefined>(min_age);
  const [maxAge, setMaxAge] = useState<number | undefined>(max_age);

  useEffect(() => {
    if (value) {
      setValueSelected(value);
    }
  }, [value]);

  const handleClickRemoveCategory = useCallback(() => {
    if (valueSelected === '') {
      onRemoveCategory();
      setValueSelected('');
    } else {
      Swal.fire({
        icon: 'warning',
        iconColor: '#ff6900',
        title: 'Gostaria de excluir a categoria?',
        showCancelButton: true,
        cancelButtonText: 'Não',
        confirmButtonText: 'Sim',
        confirmButtonColor: '#ff6900',
        cancelButtonColor: '#B34A00',
        reverseButtons: true,
      }).then((e) => {
        if (e.isConfirmed) {
          Toast.fire({
            icon: 'success',
            iconColor: '#ff6900',
            title: 'Categoria excluída com sucesso!',
          });
          onRemoveCategory();
          setValueSelected('');
        }
      });
    }
  }, [onRemoveCategory, valueSelected]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChangeCategory(e.target.value);
      setValueSelected(e.target.value);
    },
    [onChangeCategory]
  );

  const handleChangeQtyMin = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const valueMin = parseInt(e.target.value, 10);
      onChangeQtyMin(valueMin);
      setQtyMin(valueMin);
    },
    [onChangeQtyMin]
  );

  const handleChangeMinAge = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const valueMin = parseInt(e.target.value, 10);
      onChangeMinAge(valueMin);
      setMinAge(valueMin);
    },
    [onChangeMinAge]
  );

  const handleChangeMaxAge = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const valueMax = parseInt(e.target.value, 10);
      onChangeMaxAge(valueMax);
      setMaxAge(valueMax);
    },
    [onChangeMaxAge]
  );

  const isOldCategory = category.id && category.name;

  return (
    <div className="bd-modality d-flex justify-content-between align-items-center p-2 mb-2">
      <Input
        name={`category-${category.input_id}`}
        placeholder="Ex: Masculino/Feminino"
        className="border-0 p-0 input-height"
        onFocus={onFocus}
        onBlur={onBlur}
        value={valueSelected}
        onChange={handleChange}
      />
      <div className="d-flex mt-0 border-end">
        <button
          type="button"
          onClick={() => {
            setHasKit(!hasKit);
            onChangeHasKit(!hasKit);
          }}
          className="check border-0 bg-transparent mx-2"
        >
          <span className="d-block border-0">Tem&nbsp;kit?</span>
          <span
            className={`${
              hasKit ? 'checked' : ''
            } d-flex justify-content-center align-items-center mx-auto`}
          >
            {hasKit && (
              <HiCheck size={16} color="#fff" className="check-icon" />
            )}
          </span>
        </button>
      </div>
      <div className="d-flex mt-0 border-end">
        <button
          type="button"
          onClick={() => {
            setCheckChild(!checkChild);
            onChangeChild(!checkChild);
            setHasAgeGroup(false);
            onChangeHasAgeGroup(false);
          }}
          className="check border-0 bg-transparent mx-2"
        >
          <span className="d-block border-0">Criança</span>
          <span
            className={`${
              checkChild ? 'checked' : ''
            } d-flex justify-content-center align-items-center mx-auto`}
          >
            {checkChild && (
              <HiCheck size={16} color="#fff" className="check-icon" />
            )}
          </span>
        </button>
        {checkChild && (
          <div>
            <div className="d-flex mb-2">
              <span className="mx-2 small w-25">De:</span>
              <Input
                type="number"
                min={0}
                name={`min-age-${category.input_id}`}
                className="input-height rounded max-w"
                onChange={handleChangeMinAge}
                defaultValue={minAge || undefined}
              />
            </div>
            <div className="d-flex">
              <span className="mx-2 small w-25">Até:</span>
              <Input
                type="number"
                min={0}
                name={`max-age-${category.input_id}`}
                className="input-height rounded max-w"
                onChange={handleChangeMaxAge}
                defaultValue={maxAge || undefined}
              />
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={() => {
            setCheckElderly(!checkElderly);
            onChangeElderly(!checkElderly);
            setHasAgeGroup(false);
            onChangeHasAgeGroup(false);
          }}
          className="check border-0 bg-transparent mx-2"
        >
          <span className="d-block border-0">Idoso</span>
          <span
            className={`${
              checkElderly ? 'checked' : ''
            } d-flex justify-content-center align-items-center mx-auto`}
          >
            {checkElderly && (
              <HiCheck size={16} color="#fff" className="check-icon" />
            )}
          </span>
        </button>
        <button
          type="button"
          onClick={() => {
            setHasAgeGroup(!hasAgeGroup);
            onChangeHasAgeGroup(!hasAgeGroup);
            setCheckChild(false);
            onChangeChild(false);
            setCheckElderly(false);
            onChangeElderly(false);
          }}
          className="check border-0 bg-transparent mx-2"
        >
          <span className="d-block border-0">Faixa&nbsp;etária</span>
          <span
            className={`${
              hasAgeGroup ? 'checked' : ''
            } d-flex justify-content-center align-items-center mx-auto`}
          >
            {hasAgeGroup && (
              <HiCheck size={16} color="#fff" className="check-icon" />
            )}
          </span>
        </button>
        {hasAgeGroup && (
          <div className="me-2">
            <div className="d-flex mb-2">
              <span className="mx-2 small w-25">De:</span>
              <Input
                type="number"
                min={0}
                name={`min-age-${category.input_id}`}
                className="input-height rounded max-w"
                onChange={handleChangeMinAge}
                defaultValue={minAge || undefined}
              />
            </div>
            <div className="d-flex">
              <span className="mx-2 small w-25">Até:</span>
              <Input
                type="number"
                min={0}
                name={`max-age-${category.input_id}`}
                className="input-height rounded max-w"
                onChange={handleChangeMaxAge}
                defaultValue={maxAge || undefined}
              />
            </div>
          </div>
        )}
      </div>
      <div className="d-flex border-end">
        <button
          type="button"
          onClick={() => {
            const newValue = !checkMale;
            setCheckMale(newValue);
            onChangeMale(newValue);
            if (newValue) {
              setCheckFemale(false);
              onChangeFemale(false);
            }
          }}
          className="check border-0 bg-transparent mx-2"
        >
          <span className="d-block border-0">Masc</span>
          <span
            className={`${
              checkMale ? 'checked' : ''
            } d-flex justify-content-center align-items-center mx-auto`}
          >
            {checkMale && (
              <HiCheck size={16} color="#fff" className="check-icon" />
            )}
          </span>
        </button>
        <button
          type="button"
          onClick={() => {
            const newValue = !checkFemale;
            setCheckFemale(newValue);
            onChangeFemale(newValue);
            if (newValue) {
              setCheckMale(false);
              onChangeMale(false);
            }
          }}
          className="check border-0 bg-transparent mx-2"
        >
          <span className="d-block border-0">Fem</span>
          <span
            className={`${
              checkFemale ? 'checked' : ''
            } d-flex justify-content-center align-items-center mx-auto`}
          >
            {checkFemale && (
              <HiCheck size={16} color="#fff" className="check-icon" />
            )}
          </span>
        </button>
      </div>
      <div className="mx-2">
        <span className="check">Qtd. Mínima</span>
        <Input
          type="number"
          min={0}
          name={`qty-min-${category.input_id}`}
          className="input-height rounded max-w"
          onChange={handleChangeQtyMin}
          defaultValue={qtyMin || undefined}
        />
      </div>
      <FaArrowsAltV size={20} color="#8c8c8c" className="mx-3" />
      <button
        type="button"
        disabled={!!(sold && isOldCategory)}
        className={`${
          sold && isOldCategory ? 'opacity-25 not-allowed' : ''
        } bg-transparent border-0 ms-2`}
        onClick={handleClickRemoveCategory}
      >
        <KrTrash size={14} color="#B41717" className="svg-card" />
      </button>
    </div>
  );
};

export default Category;
